import bindAll from "lodash/bindAll";
import isObject from "lodash/isObject";
import extend from "lodash/extend";
import defer from "lodash/defer";
import isNumber from "lodash/isNumber";
import { Provider } from "mobx-react";
import { autorun } from "mobx";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ThemeProvider } from "styled-components";
import ResizeObserver from "resize-observer-polyfill";

import DOMUtils from "util/dom-utils";
import { render as renderReact, unmountReact } from "util/react-utils";
import AnimationFrameHandler from "./lib/animationframehandler";
import { templateFunction } from "util/componentAsString";
import { darkTheme } from "style/theme";
import {
    centerSectionToClip,
    centerSectionToLollipop
} from "util/slider-utils";

import { WaveSpinner } from "pages/common/components/state/spinner/spinner";

import TimeControlView from "./components/timecontrol/timecontrol.backbone";
import { Timeline } from "./components/timeline";
import TimeGrid from "./components/timegrid/timegrid";

import EditorTimelineTemplate from "./editortimecontrol.tpl";

var EditorTimelineView = TimeControlView.extend({
    template: templateFunction(EditorTimelineTemplate),

    className: `${TimeControlView.prototype.className} grTimeControl--composer js-composer-output editorTimeline`,

    events: extend({}, TimeControlView.prototype.events, {
        click: `focusComposition`
    }),

    initialize: function (options) {
        EditorTimelineView.__super__.initialize.apply(this, arguments);
        this.animationHandler = new AnimationFrameHandler();

        options = isObject(options) ? options : {};

        if (isObject(options.video)) {
            this.video = options.video;
        }

        this.composition = options.composition;
        this.ui = options.ui;

        this.timelineUpdateDisposer = autorun(() => {
            this.model.set(`end`, this.ui.timelineDuration);
            if (this.video.currentTime > this.composition.duration) {
                this.video.seek(this.composition.duration);
            }
            if (this.timelineWrapper) {
                // HACK - Use defer to prevent this from bveing added into undoManager history (shouldn't happen in the first place!)
                defer(() =>
                    this.ui.setTimelineWidth(this.timelineWrapper.offsetWidth)
                );
            }
        });

        bindAll(
            this,
            `onTimelineRender`,
            `bringLollipopIntoView`,
            `resizeObserverCb`,
            `mutationObserverCb`,
            `timeGridRoCb`
        );

        this.listenTo(this.model, `change:sectionStart change:sectionEnd`, () =>
            this.renderTimeGrid()
        );
    },

    onBoundariesChange: function () {
        const updateOptions = {
            range: {
                min: this.model.get(`start`),
                max: this.model.get(`end`)
            },
            limit: this.ui.maxTimelineSelection
        };

        this.timescale.noUiSlider.updateOptions(updateOptions);

        this.updateTimelineLabels();
        this.onTimescaleChange();

        return updateOptions;
    },

    onTimelineRender: function (timeline) {
        if (timeline.offsetHeight !== 0) {
            this.$timelineContainer.scrollTop(
                timeline.offsetHeight - this.$timelineContainer.height()
            );
        } else {
            setTimeout(() => this.onTimelineRender(timeline), 50);
        }
    },

    mutationObserverCb(mutations) {
        mutations.forEach((mutation) => {
            if (
                mutation.type === `attributes` &&
                mutation.attributeName === `style`
            ) {
                const { left } = this.timelineWrapper.getBoundingClientRect();
                this.ui.setTimelineOffset(left);
            }
        });
    },

    resizeObserverCb(mutations) {
        mutations.forEach((mutation) => {
            const {
                contentRect: { width }
            } = mutation;
            if (width !== this.ui.timelineWidth) {
                this.ui.setTimelineWidth(width);
            }
        });
    },

    timeGridRoCb(mutations) {
        mutations.forEach((mutation) => {
            const {
                contentRect: { width, height }
            } = mutation;
            if (
                this.timeGridWidth !== width ||
                this.timeGridHeight !== height
            ) {
                this.renderTimeGrid(width, height);
            }
        });
    },

    renderTimeGrid(width, height) {
        if (isNumber(width)) {
            this.timeGridWidth = width;
        }
        if (isNumber(height)) {
            this.timeGridHeight = height;
        }
        if (this.timeGridHeight && this.timeGridWidth) {
            renderReact(
                <TimeGrid
                    timelineModel={this.model}
                    displayLabels="top"
                    listenToClick={true}
                    seekCallback={this.video.seek}
                    parentWidth={this.timeGridWidth}
                    parentHeight={this.timeGridHeight}
                    primaryTickPosition={{
                        top: 23,
                        bottom: 45
                    }}
                    secondaryTickPosition={{
                        top: 28,
                        bottom: 40
                    }}
                />,
                this.timeGridInner
            );
        }
    },

    render: function () {
        EditorTimelineView.__super__.render.apply(this, arguments);

        this.$timelineContainer = this.$(`.js-timeline-container`);
        this.$timelineContainer.empty();
        this.timeGridInner = this.el.querySelector(`.js-timegrid-inner`);

        const { left, width } = this.timelineWrapper.getBoundingClientRect();
        this.ui.setTimelineOffset(left);
        this.ui.setTimelineWidth(width);

        if (!this.resizeObserversSet) {
            this.mutationObserver = new MutationObserver(
                this.mutationObserverCb
            );
            this.resizeObserver = new ResizeObserver(this.resizeObserverCb);
            this.timeGridRo = new ResizeObserver(this.timeGridRoCb);

            this.mutationObserver.observe(this.timelineWrapper, {
                attributes: true
            });
            this.resizeObserver.observe(this.timelineWrapper);
            this.timeGridRo.observe(this.timeGridInner);

            this.resizeObserversSet = true;
        }

        /**
         * Originally using mainTheme from styles/theme.
         * React DnD required the Timeline to be using the same theme otherwise the cloned element
         *  would render the base theme of colours from the Timeline component.
         */
        if (this.composition.layers.size > 0) {
            renderReact(
                <DndProvider backend={HTML5Backend}>
                    <ThemeProvider theme={darkTheme}>
                        <Provider ui={this.ui} composition={this.composition}>
                            <Timeline
                                renderCallback={this.onTimelineRender}
                                player={this.video}
                            />
                        </Provider>
                    </ThemeProvider>
                </DndProvider>,
                this.$timelineContainer[0]
            );
        } else {
            // HACK: If the composition doesn't have any layers, it's most likely dummy UI being displayed while the composition is loading. Show spinner
            renderReact(
                <WaveSpinner />,
                this.el.querySelector(`.js-timeline-wrapper`)
            );
        }

        this.onBoundariesChange();

        return this;
    },

    onLollipopMouseUp: function () {
        EditorTimelineView.__super__.onLollipopMouseUp.apply(this, arguments);
    },

    onLollipopDrag: function (evt) {
        let leftValue = 0;
        const contentBoundary =
            this.composition.duration / this.composition.timelineDuration;

        if (this.lollipopActive === `timeline`) {
            var timeHovered =
                DOMUtils.getTimeHovered.call(this, evt.pageX) -
                this.model.get(`start`);
            var videoLength = this.model.getTimelineDuration();
            leftValue = timeHovered / videoLength;
        } else if (this.lollipopActive === `timescale`) {
            leftValue = DOMUtils.getPercentageHovered(
                evt.pageX,
                this.$timescaleWrapper
            );
        }

        leftValue = leftValue < 0 ? 0 : leftValue;
        leftValue = leftValue > contentBoundary ? contentBoundary : leftValue;
        this.updateLollipop(leftValue);

        const time = this.getLollipopTimestamp();
        this.animationHandler.animate(() => {
            if (this.video.currentTime === time) {
                return;
            }
            this.video.seek(time);
        });
    },

    destroy: function () {
        this.timelineUpdateDisposer();
        unmountReact(this.$timelineContainer[0]);
        this.resizeObserver.disconnect();
        this.mutationObserver.disconnect();
        this.timeGridRo.disconnect();
        EditorTimelineView.__super__.destroy.apply(this, arguments);
    },

    lollipopInView: function () {
        return (
            this.model.get(`currentTime`) <= this.model.get(`sectionEnd`) &&
            this.model.get(`currentTime`) >= this.model.get(`sectionStart`)
        );
    },

    bringLollipopIntoView: function () {
        if (!this.lollipopInView()) {
            const currentTime = this.model.get(`currentTime`);
            const fiveSeconds = 5000;
            let sectionEnd = this.model.get(`sectionEnd`);
            let sectionStart = this.model.get(`sectionStart`);
            let difference;

            if (currentTime > sectionEnd) {
                difference = currentTime - sectionEnd + fiveSeconds;
            }

            if (currentTime < sectionStart) {
                difference = -(sectionStart - currentTime + fiveSeconds);
            }

            sectionStart += difference;
            sectionEnd += difference;

            this.refreshTimescale(sectionStart, sectionEnd);
        }
    },

    zoom: function (ratio, centerToClip) {
        if (this.timescale.hasAttribute(`disabled`)) {
            return;
        }

        let inPoint = this.model.get(`sectionStart`);
        let outPoint = this.model.get(`sectionEnd`);
        const inPointFinite = isFinite(inPoint);
        const outPointFinite = isFinite(outPoint);
        const start = this.model.get(`start`);
        const end = this.model.get(`end`);
        const currentTime = this.model.get(`currentTime`);

        if (!inPointFinite && outPointFinite) {
            inPoint = outPoint;
        } else if (inPointFinite && !outPointFinite) {
            outPoint = inPoint;
        } else if (!inPointFinite && !outPointFinite) {
            throw new Error(`inPoint and outPoint non finite value`);
        }

        ratio = ratio > 1 && start === 0 ? ratio * 2 : ratio;
        const newSectionLength = this.getNewSectionLength(ratio, centerToClip);

        let centeredSectionBoundaries;
        if (this.lollipopInView()) {
            centeredSectionBoundaries = centerSectionToLollipop(
                newSectionLength,
                start,
                end,
                currentTime
            );
        } else {
            centeredSectionBoundaries = centerSectionToClip(
                newSectionLength,
                inPoint,
                outPoint,
                start,
                end
            );
        }

        this.refreshTimescale(
            centeredSectionBoundaries.start,
            centeredSectionBoundaries.end
        );
        App.Events.trigger(`timeline:zoom`);
    }
});

export default EditorTimelineView;
